import { OIDCClient } from "./oidc-client.ts";

import { Common, GET_HEADERS, POST_HEADERS } from "./common.ts";
import { Utils } from "../utils/common/utils.js";

class FundAPI {
  static async getFund(fundId, toFetchAssetFunds, assetId) {
    const instanceUrl = localStorage.getItem("team");
    const url = `${instanceUrl}/services/api/fund/${fundId}`;

    try {
      const headers = await OIDCClient.prepareHeaders(GET_HEADERS);

      let fund = await fetch(url, {
        headers: headers,
        method: "GET",
        credentials: "include"
      })
        .then((resp) => Common.handleResponse(resp, "fund"))
        .then((resp) => resp.json());

      fund = {
        ...fund,
        name: fund.fundName,
        id: fund.fundId
      };

      if (!toFetchAssetFunds || !assetId) return fund;

      try {
        const assetFunds = await FundAPI.getAssetFunds(assetId);
        const assetFund = assetFunds.find((assetFund) => assetFund.fund?.fundId === fund.fundId);

        if (!assetFund) return fund;

        fund = {
          ...fund,
          acquisitionDate: assetFund.acquisitionDate,
          fdStake: assetFund.fundFDStake,
          fundFDStake: assetFund.fundFDStake,
          fundCommitment: assetFund.fundCommitment,
          commitment: assetFund.fundCommitment,
          exitDate: assetFund.exitDate
        };
      } catch (error) {
        console.log(error);
      }

      return fund;
    } catch (err) {
      throw err.message || err;
    }
  }

  static async getFunds(context, toFetchAssetFunds) {
    const instanceUrl = localStorage.getItem("team");
    const url = `${instanceUrl}/services/api/entity/funds`;

    try {
      const headers = await OIDCClient.prepareHeaders(POST_HEADERS);

      let funds = await fetch(url, {
        headers: headers,
        method: "POST",
        body: context,
        credentials: "include"
      })
        .then((resp) => Common.handleResponse(resp, "funds"))
        .then((resp) => resp.json())
        .then((data) => data.list);

      funds = funds?.map((fund) => ({
        ...fund,
        name: fund.fundName,
        id: fund.fundId
      }));

      if (!toFetchAssetFunds) return funds;

      try {
        const assetFunds = (await FundAPI.getAssetFunds(JSON.parse(context).assetId)) ?? [];

        for (const assetFund of assetFunds) {
          const index = funds.findIndex((fund) => fund.fundId === assetFund.fund?.fundId);
          if (index < 0) continue;

          funds[index] = {
            ...funds[index],
            acquisitionDate: assetFund.acquisitionDate,
            fdStake: assetFund.fundFDStake,
            fundFDStake: assetFund.fundFDStake,
            fundCommitment: assetFund.fundCommitment,
            commitment: assetFund.fundCommitment,
            exitDate: assetFund.exitDate
          };
        }
      } catch (error) {
        console.log(error);
      }

      return funds;
    } catch (err) {
      console.log(err);
      throw err.message || err;
    }
  }

  static async getAssetFunds(assetId) {
    const instanceUrl = localStorage.getItem("team");
    const url = `${instanceUrl}/services/api/asset/${assetId}/assetFunds`;

    try {
      const headers = await OIDCClient.prepareHeaders(GET_HEADERS);

      const assetFunds = await fetch(url, {
        headers: headers,
        method: "GET",
        credentials: "include"
      })
        .then((resp) => Common.handleResponse(resp, "assetFunds"))
        .then((resp) => resp.json())
        .then((data) => data.assetFund);

      return assetFunds;
    } catch (err) {
      console.log(err);
      throw err.message || err;
    }
  }

  static async getFundsOfUser() {
    const instanceUrl = localStorage.getItem("team");
    const url = `${instanceUrl}/services/api/fund`;

    try {
      const headers = await OIDCClient.prepareHeaders(GET_HEADERS);

      let funds = await fetch(url, {
        headers: headers,
        method: "GET",
        credentials: "include"
      })
        .then((resp) => Common.handleResponse(resp, "userFunds"))
        .then((resp) => resp.json())
        .then((data) => data.fund);

      funds = funds.map((fund) => ({
        ...fund,
        name: fund.fundName,
        id: fund.fundId
      }));

      return funds;
    } catch (err) {
      console.log(err);
      throw err.message || err;
    }
  }

  static async getFundUpdateTypes() {
    const instanceUrl = localStorage.getItem("team");
    const url = `${instanceUrl}/services/api/update/types/fund`;

    try {
      const headers = await OIDCClient.prepareHeaders(GET_HEADERS);

      let fundUpdateTypes = await fetch(url, {
        headers: headers,
        method: "GET",
        credentials: "include"
      })
        .then((resp) => Common.handleResponse(resp, "fund update types"))
        .then((resp) => resp.json())
        .then((resp) => resp.fundUpdateType ?? []);

      fundUpdateTypes = fundUpdateTypes.map((updateType) => ({
        ...updateType,
        name: updateType.fundUpdateType,
        type: updateType.fundUpdateType,
        id: updateType.fundUpdateTypeId
      }));

      return fundUpdateTypes;
    } catch (err) {
      console.log(err);
      throw err.message || err;
    }
  }

  static async getFundUpdates(fundId, toDate, fromDate, categoryId) {
    const instanceUrl = localStorage.getItem("team");
    let url = `${instanceUrl}/services/api/update/fund/${fundId}?toDate=${toDate}&fromDate=${fromDate}`;
    if (categoryId) url += `&updateTypeId=${categoryId}`;
    url += "&statusId=5";

    try {
      const headers = await OIDCClient.prepareHeaders(GET_HEADERS);
      let fundUpdates = await fetch(url, {
        headers: headers,
        method: "GET",
        credentials: "include"
      })
        .then((resp) => Common.handleResponse(resp, "fund updates"))
        .then((resp) => resp.json())
        .then((resp) => resp.fundUpdate ?? []);

      fundUpdates = fundUpdates.map((update) => ({
        ...update,
        text: update.updateText,
        name: update.updateText,
        period: { ...update.period, date: Utils.getExcelDateFromJS(update.period?.date, true) }
      }));

      return fundUpdates;
    } catch (err) {
      console.log(err);
      throw err.message || err;
    }
  }
}

export { FundAPI };
